import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

// Styled components
const WalletContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: ${({ theme, lowBalance }) => 
    lowBalance ? 'rgba(238, 76, 80, 0.1)' : 'rgba(0, 123, 255, 0.1)'};
  border: 1px solid ${({ theme, lowBalance }) => 
    lowBalance ? '#ee4c50' : '#007bff'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme, lowBalance }) => 
      lowBalance ? 'rgba(238, 76, 80, 0.2)' : 'rgba(0, 123, 255, 0.2)'};
  }

  @media (max-width: 768px) {
    padding: 6px 8px;
    gap: 6px;
  }
`;

const WalletIcon = styled.span`
  font-size: 18px;
  color: ${({ lowBalance }) => lowBalance ? '#ee4c50' : '#007bff'};
`;

const BalanceText = styled.span`
  font-family: 'Poppins Medium', sans-serif;
  font-size: 14px;
  color: ${({ theme, lowBalance }) => 
    lowBalance ? '#ee4c50' : theme.color.primaryText};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TopUpButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #218838;
  }

  @media (max-width: 768px) {
    padding: 3px 6px;
    font-size: 10px;
  }
`;

const WalletWidget = ({ 
  userId, 
  onBalanceUpdate, 
  showTopUpButton = true,
  minBalance = 1000,
  refreshInterval = 30000 // 30 seconds
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState('GCOIN');

  // Check if balance is low
  const isLowBalance = balance < minBalance;

  // Fetch balance from wallet service
  const fetchBalance = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      console.log('🔄 Fetching wallet balance for user:', userId);
      
      const response = await axios.get(`${ENV_CONFIG.WALLET_BALANCE_ENDPOINT}/${userId}`, {
        timeout: 5000,
        withCredentials: true
      });

      const newBalance = parseFloat(response.data.balance) || 0;
      const walletCurrency = response.data.currency || 'GCOIN';
      
      setBalance(newBalance);
      setCurrency(walletCurrency);
      setError(null);
      
      // Notify parent component of balance update
      if (onBalanceUpdate) {
        onBalanceUpdate(newBalance, walletCurrency);
      }

      console.log('✅ Wallet balance updated:', newBalance, walletCurrency);

    } catch (err) {
      console.error('❌ Error fetching wallet balance:', err);
      
      if (err.response?.status === 404) {
        setError('Wallet not found');
      } else if (err.response?.status >= 500) {
        setError('Service unavailable');
      } else {
        setError('Failed to load balance');
      }
      
      // Don't show toast for every error, just log it
      if (err.response?.status !== 404) {
        toast.error('Erreur lors du chargement du solde');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, onBalanceUpdate]);

  // Initial fetch and setup polling
  useEffect(() => {
    if (!userId) return;

    // Initial fetch
    fetchBalance();

    // Setup polling if refreshInterval is provided
    let interval;
    if (refreshInterval > 0) {
      interval = setInterval(fetchBalance, refreshInterval);
    }

    // Cleanup
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [userId, fetchBalance, refreshInterval]);

  // Handle top-up button click
  const handleTopUp = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    try {
      localStorage.setItem('postGcoinPurchaseRedirect', returnTo);
    } catch (_) {
      // ignore
    }
    const params = new URLSearchParams({
      redirect: returnTo,
      reason: 'topup'
    });
    history.push(`/gcoin-store?${params.toString()}`);
  };

  // Handle wallet click (show transaction history)
  const handleWalletClick = () => {
    const historyUrl = `/wallet/history?userId=${userId}`;
    window.open(historyUrl, '_blank');
  };

  // Format balance for display
  const formatBalance = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toLocaleString();
  };

  // Don't render if no userId
  if (!userId) {
    return null;
  }

  return (
    <WalletContainer 
      lowBalance={isLowBalance} 
      onClick={handleWalletClick}
      title={`Solde: ${balance} ${currency}${isLowBalance ? ' (Solde faible)' : ''}`}
    >
      <WalletIcon lowBalance={isLowBalance}>
        💰
      </WalletIcon>
      
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <BalanceText lowBalance={true}>
          ❌ {error}
        </BalanceText>
      ) : (
        <BalanceText lowBalance={isLowBalance}>
          {formatBalance(balance)} {currency}
        </BalanceText>
      )}
      
      {showTopUpButton && isLowBalance && !loading && !error && (
        <TopUpButton 
          onClick={(e) => {
            e.stopPropagation();
            handleTopUp();
          }}
          title={t('nav.buyGCoins', 'Buy G-Coins')}
        >
          {t('nav.buyGCoins', 'Buy G-Coins')}
        </TopUpButton>
      )}
    </WalletContainer>
  );
};

// Higher-order component for easy integration
export const withWallet = (WrappedComponent) => {
  return (props) => {
    const [userBalance, setUserBalance] = useState(0);
    const [walletCurrency, setWalletCurrency] = useState('GCOIN');

    const handleBalanceUpdate = (balance, currency) => {
      setUserBalance(balance);
      setWalletCurrency(currency);
    };

    return (
      <WrappedComponent
        {...props}
        userBalance={userBalance}
        walletCurrency={walletCurrency}
        onBalanceUpdate={handleBalanceUpdate}
      />
    );
  };
};

export default WalletWidget;
